import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';

const SITE_URL  = 'https://construcciones-remalex.com';
const SITE_NAME = 'Construcciones Remalex';
const DEFAULT_IMAGE = `${SITE_URL}/hero-villa.jpg`;

// Per-language defaults — used when the page does not pass title/description.
const DEFAULTS_BY_LANG = {
  fr: {
    title: 'Construcciones Remalex · Construction de villas, Costa Brava',
    description: "Construction neuve, rénovation et piscines pour propriétaires de villas sur la Costa Brava. Platja d'Aro, Girona. Devis gratuit, réponse sous 24h.",
  },
  en: {
    title: 'Construcciones Remalex · Villa construction, Costa Brava',
    description: "New build, renovation and pools for villa owners on the Costa Brava. Platja d'Aro, Girona. Free quote, reply within 24h.",
  },
  de: {
    title: 'Construcciones Remalex · Villenbau an der Costa Brava',
    description: "Neubau, Renovierung und Pools für Villenbesitzer an der Costa Brava. Platja d'Aro, Girona. Kostenloses Angebot, Antwort innerhalb von 24 Std.",
  },
  es: {
    title: 'Construcciones Remalex · Construcción de villas, Costa Brava',
    description: "Obra nueva, reformas y piscinas para propietarios de villas en la Costa Brava. Platja d'Aro, Girona. Presupuesto gratuito, respuesta en 24 h.",
  },
  ru: {
    title: 'Construcciones Remalex · Строительство вилл, Коста-Брава',
    description: "Новое строительство, реновация и бассейны для владельцев вилл на Коста-Браве. Platja d'Aro, Жирона. Бесплатная смета, ответ в течение 24 часов.",
  },
};

// Hreflang alternates — same path, different lang param
const HREFLANG_LANGS = ['fr', 'en', 'de', 'es', 'ru'];
const OG_LOCALE_MAP  = { fr: 'fr_FR', en: 'en_GB', de: 'de_DE', es: 'es_ES', ru: 'ru_RU' };

/**
 * SEO component — place at the top of every public page.
 *
 * The `lang` prop is optional — when omitted, the component picks up the
 * current language from LanguageContext. Pass `lang` explicitly only when
 * the page is locked to a specific language (PDFs, signature flows…).
 */
export default function SEO({ title, description, canonical, image, lang: langProp, noindex = false, type = 'website', jsonLd = null }) {
  const { language } = useLanguage();
  const lang = langProp || language || 'fr';
  const defaults = DEFAULTS_BY_LANG[lang] || DEFAULTS_BY_LANG.fr;

  const t   = title       || defaults.title;
  const d   = description || defaults.description;
  const img = image       || DEFAULT_IMAGE;
  const fullImg = img && img.startsWith('http') ? img : `${SITE_URL}${img}`;
  const url = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const ogLocale = OG_LOCALE_MAP[lang] || 'fr_FR';
  const alternateLocales = Object.values(OG_LOCALE_MAP).filter(l => l !== ogLocale);

  return (
    <Helmet>
      {/* ── Core ── */}
      <title>{t}</title>
      <meta name="description" content={d} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* ── hreflang — crucial pour le SEO multilingue ── */}
      {HREFLANG_LANGS.map(l => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={`${url}${url.includes('?') ? '&' : '?'}lang=${l}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* ── Open Graph ── */}
      <meta property="og:type"        content={type} />
      <meta property="og:url"         content={url} />
      <meta property="og:site_name"   content={SITE_NAME} />
      <meta property="og:title"       content={t} />
      <meta property="og:description" content={d} />
      <meta property="og:image"       content={fullImg} />
      <meta property="og:locale"      content={ogLocale} />
      {alternateLocales.map(l => (
        <meta key={l} property="og:locale:alternate" content={l} />
      ))}

      {/* ── Twitter Card ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={t} />
      <meta name="twitter:description" content={d} />
      <meta name="twitter:image"       content={fullImg} />

      {/* ── Schema.org JSON-LD ── */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
